'use client';

import { TeamMatchScore } from '@/components/tables/match-play/match-shared';
import { ElasticCourseData, ElasticMatchplayGroupsGroupsInner, ElasticMatchplayMatches, ElasticTournament, MatchplayGroupsGroupsInnerTeamsInner, TournamentEventTypeEnum, TournamentRequestFourpeopleTypesEnum } from '@spikemark/rest-api';
import { useFetch } from '@spikemark/shared-hooks';
import { ExpandableComponentContainer } from '@spikemark/ui/components/expandable-content-container';
import { Button } from '@spikemark/ui/components/ui/button';
import noop from 'lodash/noop';
import startCase from 'lodash/startCase';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { MatchSummary, MatchSummaryPlayerResult } from './match-summary';
import { TeamMatchTracker } from './team-match-tracker';
export type MatchSummaryProps = {
  tournament: ElasticTournament;
  tournamentId: string;
  roundId: number;
  format: TournamentEventTypeEnum;
  matchplayFormat: TournamentRequestFourpeopleTypesEnum;
  leaderboard: ElasticMatchplayGroupsGroupsInner;
  hideExpander?: boolean;
  teamA?: MatchplayGroupsGroupsInnerTeamsInner;
  teamB?: MatchplayGroupsGroupsInnerTeamsInner;
  courseSetup: ElasticCourseData[];
  pollingInterval?: number;
};
const ExpandedComponent = ({
  tournament,
  tournamentId,
  roundId,
  format,
  groupNum,
  teamA,
  teamB,
  courseSetup,
  pollingInterval,
  isExpanded,
  matchplayFormat
}) => {
  const {
    data,
    error,
    isLoading
  } = useFetch<ElasticMatchplayMatches, {
    groupNum: number;
    roundId: number;
  }>(`/api/tournaments/${tournamentId}/matchplay/matches`, 'GET', {
    params: {
      groupNum,
      roundId
    },
    pollingInterval
  });
  return <ExpandableComponentContainer expanded={isExpanded} isLoading={isLoading} data-sentry-element="ExpandableComponentContainer" data-sentry-component="ExpandedComponent" data-sentry-source-file="team-match-summary.tsx">
      {data?.matches?.map((match, index) => <MatchSummary key={match.matchId} tournament={tournament} format={format} matchplayFormat={matchplayFormat} match={{
      ...match,
      matchId: index + 1
    }} teamA={teamA} teamB={teamB} courseSetup={courseSetup} />)}
    </ExpandableComponentContainer>;
};
export const TeamMatchSummary: FC<MatchSummaryProps> = ({
  tournament,
  tournamentId,
  roundId,
  format,
  matchplayFormat,
  leaderboard,
  hideExpander,
  teamA,
  teamB,
  courseSetup,
  pollingInterval
}) => {
  const [isExpanded, setisExpanded] = useState<boolean>(false);
  const expanderFn = hideExpander ? noop : setisExpanded;
  const teamAScore = teamA?.teamPoints || 0;
  const teamBScore = teamB?.teamPoints || 0;
  const title = leaderboard.matchName || matchplayFormat || format;
  const result = useMemo(() => [{
    score: teamAScore,
    name: teamA?.schoolName || '',
    color: teamA?.schoolColour
  }, {
    score: teamBScore,
    name: teamB?.schoolName || '',
    color: teamB?.schoolColour
  }], [teamA, teamB, teamAScore, teamBScore]);
  const tracker = <TeamMatchTracker result={result} />;
  const matchHasStarted = leaderboard.teamScore !== '0-0-0';
  return <>
      <div className="outline w-full outline-gray-200 shadow-md rounded-[8px] overflow-clip flex flex-col bg-white text-gray-700 mb-4 mt-4 first:mt-0">
        <div className="flex items-stretch">
          <MatchSummaryPlayerResult side="left" name={teamA?.schoolName ?? ''} score={''} lose={teamAScore <= teamBScore} schoolLogo={teamA?.schoolLogo || ''} schoolColor={teamA?.schoolColour} data-sentry-element="MatchSummaryPlayerResult" data-sentry-source-file="team-match-summary.tsx" />
          <div className="flex-1 flex flex-col items-center justify-between p-2 lg:p-4">
            {title && <h3 className="text-sm text-center font-bold text-gray-600">
                {startCase(title.toLowerCase())}
              </h3>}
            <TeamMatchScore result={result} matchHasStarted={matchHasStarted} teeTime={leaderboard.teeTime} data-sentry-element="TeamMatchScore" data-sentry-source-file="team-match-summary.tsx" />
            <div className="hidden lg:block w-full mt-3">{tracker}</div>
          </div>
          <MatchSummaryPlayerResult side="right" name={teamB?.schoolName ?? ''} score={''} lose={teamBScore <= teamAScore} schoolLogo={teamB?.schoolLogo || ''} schoolColor={teamB?.schoolColour} data-sentry-element="MatchSummaryPlayerResult" data-sentry-source-file="team-match-summary.tsx" />
        </div>
        <div className="lg:hidden px-2 pt-2 lg:pb-2">{tracker}</div>
        {!hideExpander && <div className="text-center lg:py-1 lg:border-t border-gray-200">
            <Button variant="ghost" size="sm" onClick={() => expanderFn(!isExpanded)}>
              {isExpanded ? <>
                  Hide matches
                  <ChevronUp className="ml-1" size={18} />
                </> : <>
                  Show matches
                  <ChevronDown className="ml-1" size={18} />
                </>}
            </Button>
          </div>}
      </div>
      <ExpandedComponent tournament={tournament} format={format} courseSetup={courseSetup} roundId={roundId} tournamentId={tournamentId} groupNum={leaderboard.groupNum} teamA={teamA} teamB={teamB} pollingInterval={pollingInterval} isExpanded={isExpanded} matchplayFormat={matchplayFormat} data-sentry-element="ExpandedComponent" data-sentry-source-file="team-match-summary.tsx" />
    </>;
};